import { useEffect } from "react";
import { useNavigate } from "react-router";

import { t } from "i18next";

import { notificationAtom } from "@/src/state/notificationAtom";

import { useAuth } from "@/src/auth/hooks/useAuth";

import { useSetAtom } from "jotai";

interface UsePublicRouteRedirectProps {
  routeType: "consumer" | "dealer" | "admin";
}

export const usePublicRouteRedirect = ({
  routeType
}: UsePublicRouteRedirectProps) => {
  const { isAuthenticating, isAuthenticated, isDealer, isConsumer, isAdmin } =
    useAuth();
  const navigate = useNavigate();
  const setNotification = useSetAtom(notificationAtom);

  const dealerTryingToAccessConsumerRoute =
    isAuthenticated && isDealer && routeType === "consumer";
  const consumerTryingToAccessDealerRoute =
    isAuthenticated && isConsumer && routeType === "dealer";
  const adminTryingToAccessNonAdminRoute =
    isAuthenticated && isAdmin && routeType !== "admin";
  const nonAdminTryingToAccessAdminRoute =
    isAuthenticated && !isAdmin && routeType === "admin";

  useEffect(() => {
    if (isAuthenticating) {
      return;
    }

    if (dealerTryingToAccessConsumerRoute) {
      setNotification({
        text: t("notifications.wrongAccountTypeDealer"),
        variant: "info"
      });
      navigate("/dealer");
      return;
    }

    if (consumerTryingToAccessDealerRoute) {
      setNotification({
        text: t("notifications.wrongAccountTypeConsumer"),
        variant: "info"
      });
      navigate("/");
      return;
    }

    if (adminTryingToAccessNonAdminRoute) {
      setNotification({
        text: t("notifications.logoutFirst"),
        variant: "info"
      });
      navigate("/admin");
      return;
    }

    if (nonAdminTryingToAccessAdminRoute) {
      setNotification({
        text: t("notifications.logoutFirst"),
        variant: "info"
      });
      navigate(isDealer ? "/dealer" : "/");
      return;
    }
  }, [
    isAuthenticating,
    isAuthenticated,
    isDealer,
    isConsumer,
    isAdmin,
    routeType,
    navigate,
    setNotification
  ]);

  return {
    redirectNeeded:
      dealerTryingToAccessConsumerRoute ||
      consumerTryingToAccessDealerRoute ||
      adminTryingToAccessNonAdminRoute ||
      nonAdminTryingToAccessAdminRoute
  };
};
